/**
 * Per-publication device addressing: where *this* delivery of the lab reaches each box.
 *
 * The reason this table exists. A section is attached to several labs, and the same section is
 * published into several data centres - so the address a device answers on cannot live only on the
 * device. RTP hands out one range and London another, and a section written against RTP's would be
 * unreachable everywhere else. Copying the section to fix that is what the link tables were built to
 * avoid, so the address is overridden per publication instead.
 *
 * Only the address and the port. Not the device type: the type is what an item was written against,
 * and a publication that could change it would be a publication that could quietly point a
 * firewall item at a Windows host. That is a different edit, and it belongs on the device.
 *
 * The effective value is shown beside the default on every row, overridden or not, because the
 * question an admin has here is never "what does this row say" - it is "what will the run use".
 */

import React, { useMemo, useState } from 'react';
import TextField from '@mui/material/TextField';
import StbButton from '../security-toolbox-ui/StbButton';
import { useDeleteLabRow, useSaveLabRow } from '../../hooks/lab-hooks';
import {
  LabConfiguration,
  LabDeviceAddress,
  LabDevice,
  labDeviceAddressIsRedundant,
  labEffectiveDeviceAddress,
  LAB_TABLES,
} from '../../support/labService';
import { LabMessage, LabPanelState, labErrorMessage } from './LabShared';
import { LabSection, useLabToasts } from './LabAdminShared';

export type LabDeviceAddressPanelProps = {
  /** The publication being addressed: one lab in one data centre. */
  configuration: LabConfiguration;
  /**
   * Every device the lab's sections bring, with the section named on each. Assembled by the caller,
   * which already has the section list - this panel would otherwise need a fetch per scenario.
   */
  devices: (LabDevice & { scenario_name?: string })[];
  /** The override rows for this publication. `configuration.device_addresses` when it was fetched. */
  overrides: LabDeviceAddress[];
  loading?: boolean;
  error?: unknown;
  canEdit?: boolean;
  className?: string;
};

type DraftRow = { address: string; port: string };

const LabDeviceAddressPanel: React.FC<LabDeviceAddressPanelProps> = ({
  configuration,
  devices,
  overrides,
  loading,
  error,
  canEdit = true,
  className = '',
}) => {
  const [drafts, setDrafts] = useState<Record<number, DraftRow>>({});
  const [rowError, setRowError] = useState<Record<number, string | null>>({});

  const toasts = useLabToasts();
  const save = useSaveLabRow<Partial<LabDeviceAddress> & { id?: number }>(LAB_TABLES.deviceAddress);
  const remove = useDeleteLabRow(LAB_TABLES.deviceAddress);

  const overrideOf = useMemo(() => {
    const map = new Map<number, LabDeviceAddress>();

    overrides
      .filter((row) => row.configuration === configuration.id)
      .forEach((row) => map.set(row.device, row));

    return map;
  }, [overrides, configuration.id]);

  /** Grouped by section, because that is how somebody reads a lab's devices. */
  const grouped = useMemo(() => {
    const sections = new Map<string, typeof devices>();

    devices.forEach((device) => {
      const key = device.scenario_name || 'Unattributed';

      sections.set(key, [...(sections.get(key) || []), device]);
    });

    return [...sections.entries()].map(([name, rows]) => ({
      name,
      rows: [...rows].sort((left, right) => (left.order || 0) - (right.order || 0)),
    }));
  }, [devices]);

  const draftFor = (device: LabDevice): DraftRow => {
    const existing = drafts[device.id];

    if (existing) return existing;

    const override = overrideOf.get(device.id);

    return {
      address: override?.address_template || '',
      port: override?.port === null || override?.port === undefined ? '' : String(override.port),
    };
  };

  /**
   * Seeded from the override on first edit, so typing in the port does not blank the address.
   *
   * The draft only exists once a field has been touched; until then the row reads straight off the
   * saved override, which is what makes "nothing is dirty" the honest default after a save.
   */
  const setDraft = (device: LabDevice, patch: Partial<DraftRow>) => {
    const seed = draftFor(device);

    setRowError((current) => ({ ...current, [device.id]: null }));
    setDrafts((current) => ({
      ...current,
      [device.id]: { ...seed, ...patch },
    }));
  };

  const commit = async (device: LabDevice) => {
    const draft = draftFor(device);
    const override = overrideOf.get(device.id);
    const address = draft.address.trim();
    const port = draft.port.trim();

    /**
     * An empty row is a deleted override, not an override to the empty string.
     *
     * Otherwise clearing a field would pin the device to no address at all, which reads on the row
     * as "back to the default" and behaves as "unreachable".
     */
    if (!address && !port) {
      if (!override) return;

      try {
        await remove.mutateAsync(override.id);
        setDrafts((current) => {
          const next = { ...current };
          delete next[device.id];
          return next;
        });
        toasts.said(
          `${device.device_key} is back to its own address in ${configuration.name}.`,
        );
      } catch (exc) {
        setRowError((current) => ({
          ...current,
          [device.id]: labErrorMessage(exc, 'That override could not be removed.'),
        }));
      }

      return;
    }

    if (port && !/^\d+$/.test(port)) {
      setRowError((current) => ({
        ...current,
        [device.id]: 'The port has to be a number, or empty to inherit the device\'s own.',
      }));

      return;
    }

    try {
      await save.mutateAsync({
        ...(override ? { id: override.id } : {}),
        configuration: configuration.id,
        device: device.id,
        address_template: address,
        // Empty means inherit, and null is how that is recorded - a 0 would be a real port number
        // the runtime would try to use.
        port: port === '' ? null : Number(port),
        notes: override?.notes || '',
      });

      setDrafts((current) => {
        const next = { ...current };
        delete next[device.id];
        return next;
      });
      toasts.saved(`how ${device.device_key} is reached in ${configuration.name}`);
    } catch (exc) {
      setRowError((current) => ({
        ...current,
        [device.id]: labErrorMessage(exc, 'That address could not be saved.'),
      }));
    }
  };

  const redundantCount = useMemo(
    () =>
      [...overrideOf.values()].filter((row) => labDeviceAddressIsRedundant(row)).length,
    [overrideOf],
  );

  return (
    <div className={className}>
      <LabSection
        title={`Addressing in ${configuration.data_center_name || 'this data centre'}`}
        hint={`What ${configuration.name} reaches each device at. Empty means the device's own address is used - which is right whenever this data centre hands out the same range the section was written against.`}
      >
      <LabPanelState
        loading={loading}
        error={error}
        empty={!devices.length}
        emptyMessage="This lab's sections bring no devices, so there is nothing to address."
        loadingLabel="Loading the lab's devices..."
      >
        <div className="flex flex-col gap-4">
          {/* Flagged rather than cleaned up automatically: a redundant row is not wrong, it is a
              pin. Somebody later "fixes" the device address, nothing changes, and this is why. */}
          {redundantCount > 0 && (
            <LabMessage tone="warning">
              {`${
                redundantCount === 1 ? 'One override states' : `${redundantCount} overrides state`
              } exactly what the device already says. They are harmless until somebody changes the device and finds this publication unaffected - clear them to follow the device again.`}
            </LabMessage>
          )}

          {grouped.map((section) => (
            <div key={section.name}>
              <p className="m-0 mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                {section.name}
              </p>

              <div className="overflow-hidden rounded border border-gray-200">
                <table className="w-full border-collapse text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-3 py-2 font-semibold">Device</th>
                      <th className="px-3 py-2 font-semibold">Its own address</th>
                      <th className="px-3 py-2 font-semibold">Reached here at</th>
                      <th className="px-3 py-2 font-semibold">Port</th>
                      <th className="px-3 py-2 font-semibold">What the run will use</th>
                      {canEdit && <th className="px-3 py-2" />}
                    </tr>
                  </thead>

                  <tbody>
                    {section.rows.map((device) => {
                      const override = overrideOf.get(device.id) || null;
                      const draft = draftFor(device);
                      const dirty =
                        !!drafts[device.id] &&
                        (draft.address !== (override?.address_template || '') ||
                          draft.port !==
                            (override?.port === null || override?.port === undefined
                              ? ''
                              : String(override.port)));
                      const effective = labEffectiveDeviceAddress(device, override);
                      const message = rowError[device.id];

                      return (
                        <tr className="border-t border-gray-100 align-top" key={device.id}>
                          <td className="px-3 py-2">
                            <span className="font-medium">
                              {device.display_name || device.device_key}
                            </span>
                            <span className="block text-xs text-gray-500">
                              {[
                                device.device_type_name || device.device_type_key,
                                device.product_name || null,
                                device.transport,
                              ]
                                .filter(Boolean)
                                .join(' • ')}
                            </span>

                            {/* Said on the row, because the override is narrower than the box: the
                                device is one library entry attached to several sections, and this
                                re-addresses it for this publication only - not for the other labs
                                those sections are in. */}
                            {(device.scenario_links || []).length > 1 && (
                              <span className="block text-xs text-gray-500">
                                {`Shared by ${
                                  (device.scenario_links || []).length
                                } sections. This address applies to this publication only.`}
                              </span>
                            )}
                          </td>

                          <td className="px-3 py-2 font-mono text-xs text-gray-600">
                            {device.address_template || 'none'}
                            {device.port ? `:${device.port}` : ''}
                          </td>

                          <td className="px-3 py-2">
                            <TextField
                              value={draft.address}
                              onChange={(evt) => setDraft(device, { address: evt.target.value })}
                              placeholder="Leave empty to follow the device"
                              size="small"
                              disabled={!canEdit}
                              fullWidth
                            />
                          </td>

                          <td className="px-3 py-2" style={{ width: 110 }}>
                            <TextField
                              value={draft.port}
                              onChange={(evt) => setDraft(device, { port: evt.target.value })}
                              placeholder="inherit"
                              size="small"
                              disabled={!canEdit}
                              fullWidth
                            />
                          </td>

                          <td className="px-3 py-2 text-xs">
                            <span
                              className={`font-mono ${
                                effective.address ? 'text-gray-700' : 'text-amber-800'
                              }`}
                            >
                              {effective.address || 'nothing - unreachable'}
                              {effective.port ? `:${effective.port}` : ''}
                            </span>
                            <span className="block text-gray-500">
                              {effective.overridden
                                ? 'Overridden for this publication.'
                                : 'The device\'s own.'}
                            </span>
                            {message && (
                              <span className="block text-amber-800">{message}</span>
                            )}
                          </td>

                          {canEdit && (
                            <td className="px-3 py-2 text-right">
                              <StbButton
                                role="default"
                                size="xsmall"
                                disabled={!dirty || save.isPending || remove.isPending}
                                onClick={() => commit(device)}
                              >
                                {!draft.address.trim() && !draft.port.trim() && override
                                  ? 'Clear'
                                  : 'Save'}
                              </StbButton>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          <p className="m-0 text-xs text-gray-500">
            Only the address and port are overridable. The device type is what its items were written
            against, so changing it here would let one data centre point a firewall item at a
            different kind of box - that edit belongs on the device, where every publication sees it.
          </p>
        </div>
        </LabPanelState>
      </LabSection>
    </div>
  );
};

export default LabDeviceAddressPanel;
